import { TypedDocumentNode, gql } from '@apollo/client';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { Outlet } from 'react-router-dom';
import styled from 'styled-components';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import {
  glossary,
  rivals as glossaryRivals,
  navLabels,
} from '@sorare/core/src/lib/glossary';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { RivalsProfile } from 'components/rivals/RivalsProfile';
import { LeaderboardStats } from 'components/rivals/RivalsProfile/LeaderboardStats';
import pattern from 'components/rivals/RivalsProfile/assets/pattern.svg';

import { ProfileLayout_footballRivalsRoot } from './__generated__/index.graphql';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  position: relative;
  isolation: isolate;

  @media ${desktopAndAbove} {
    flex-direction: row;
    padding: var(--triple-unit);
    gap: var(--triple-unit);
    background: none;
    height: 100%;
    max-height: var(--area-height);
  }
`;
const Background = styled.div`
  background: center / cover url(${pattern});

  @media ${desktopAndAbove} {
    flex: 1;
  }
`;
const RivalsProfileWrapper = styled.div`
  @media ${desktopAndAbove} {
    height: 100%;
    position: sticky;
    top: 0;
  }
`;
const TabsWrapper = styled(Vertical).attrs({ gap: 0 })`
  @media ${desktopAndAbove} {
    border: 1px solid var(--c-nd-150);
    border-radius: var(--double-unit);
    max-width: 424px;
    overflow-x: hidden;
    flex: 1;
  }
`;
const IconWrapper = styled(Horizontal)`
  padding: var(--double-unit);
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;

  @media ${desktopAndAbove} {
    display: none;
  }
`;
const OutletWrapper = styled.div`
  padding: var(--double-unit);
  height: 100%;
`;

type Props = {
  backPath: string;
  arenaPath: string;
  matchesPath: string;
  rivals: Nullable<ProfileLayout_footballRivalsRoot>;
};
export const ProfileLayout = ({
  backPath,
  arenaPath,
  matchesPath,
  rivals,
}: Props) => {
  const { formatMessage } = useIntl();
  const isDesktopAndAbove = useIsDesktopAndAbove();

  if (!rivals) {
    return null;
  }

  return (
    <FullWidthContainer.NoPadding>
      <Root>
        <Background>
          <IconWrapper>
            <IconButton
              color="tertiary"
              icon={faXmark}
              aria-label={formatMessage(glossary.close)}
              to={backPath}
              replace
            />
          </IconWrapper>
          <RivalsProfileWrapper>
            <RivalsProfile rivals={rivals} withBackground={isDesktopAndAbove}>
              <LeaderboardStats manager={rivals.manager} />
            </RivalsProfile>
          </RivalsProfileWrapper>
        </Background>
        <TabsWrapper>
          <div>
            <TabBar variant="flat" fullWidth>
              <Tab to={arenaPath} replace>
                <FormattedMessage {...glossaryRivals.arena} />
              </Tab>
              <Tab to={matchesPath} replace>
                <FormattedMessage {...navLabels.matches} />
              </Tab>
            </TabBar>
          </div>
          <OutletWrapper>
            <Outlet />
          </OutletWrapper>
        </TabsWrapper>
      </Root>
    </FullWidthContainer.NoPadding>
  );
};

ProfileLayout.fragments = {
  footballRivalsRoot: gql`
    fragment ProfileLayout_footballRivalsRoot on FootballRivalsRoot {
      id
      manager(userSlug: $user) {
        id
        ...LeaderboardStats_footballRivalsManager
      }
      ...RivalsProfile_footballRivalsRoot
    }
    ${RivalsProfile.fragments.footballRivalsRoot}
    ${LeaderboardStats.fragments.footballRivalsManager}
  ` as TypedDocumentNode<ProfileLayout_footballRivalsRoot>,
};
